import type { ProviderVersionFinding } from '@/lib/apiTypes'
import { SEVERITY_COLOR } from '@/lib/format'
import { isSafeHttpsUrl } from '@/features/providers/lib/urls'
import { CveLink } from '@/features/providers/tabs/version/VersionExpansion'

/** The six shared finding cells (CVE · severity · package · fixed-in · fixable · CVSS) rendered
 *  by both the per-version and aggregated findings tables. Accepts anything finding-shaped, so the
 *  aggregated (de-duped) rows reuse the exact same markup. */
export function FindingCells({ f }: { f: ProviderVersionFinding }) {
  return (
    <>
      <td style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem' }}>
        {f.cve_id
          // CVE-/GHSA- ids always link to their registry (NVD / GitHub Advisories): Trivy's
          // primary_url points at avd.aquasec.com, which 404s for CVEs Aqua has not ingested yet
          // (e.g. CVE-2026-56854 on 2026-09-06). The scanner URL is only the fallback for ids the
          // registries do not host (GO-*, PYSEC-*, ...), and only when it is actually safe to
          // navigate to - primary_url comes straight from the scanner's JSON meta, unvalidated.
          ? (/^(CVE|GHSA)-/i.test(f.cve_id) || !isSafeHttpsUrl(f.primary_url)
              ? <CveLink id={f.cve_id} />
              : <a href={f.primary_url} target="_blank" rel="noopener noreferrer">{f.cve_id}</a>)
          : <span className="dim">—</span>}
      </td>
      <td>
        <span style={{ color: SEVERITY_COLOR[f.severity] ?? 'var(--text2)', fontSize: '0.78rem', textTransform: 'capitalize' }}>
          {f.severity}
        </span>
      </td>
      <td style={{ fontSize: '0.78rem' }}>
        {f.pkg_name ?? <span className="dim">—</span>}
        {f.installed_version ? <span className="dim"> @ {f.installed_version}</span> : ''}
      </td>
      <td style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem' }}>{f.fixed_version ?? '—'}</td>
      <td>{f.fixable ? <span style={{ color: 'var(--green)' }}>Yes</span> : <span className="dim">No</span>}</td>
      <td style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem' }}>{f.cvss_score ?? '—'}</td>
    </>
  )
}
